import React, { useState } from "react";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { formatDate } from "common/service/CommonService";
import { formatTime24Hour, renderDeviationBadge } from "modules/Session/Session/helper/sessionlogconstant";
import { ScanSampleCollectionSummaryItem } from "../helper/session.types";
import TotalAttemptsHistoryModal from "./TotalAttemptsHistoryModal";
import { renderResultBadge } from "../helper/attemptsConstant";
import ac from "../css/accessioning.module.scss";

interface SampleCollectionHistoryTabProps {
  data: ScanSampleCollectionSummaryItem[];
  loading: boolean;
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
}

const SampleCollectionHistoryTab: React.FC<SampleCollectionHistoryTabProps> = ({
  data,
  loading,
  currentPage,
  pageSize,
  totalRecords,
  onPageChange,
}) => {
  const [selectedAttemptRow, setSelectedAttemptRow] = useState<any>(null);
  const [isAttemptsModalOpen, setIsAttemptsModalOpen] = useState<boolean>(false);

  const handleOpenAttemptsModal = (row: any) => {
    setSelectedAttemptRow(row);
    setIsAttemptsModalOpen(true);
  };

  const columns = [
    {
      key: "subjectno",
      label: "Subject No",
      width: "100px",
      textCenter: true,
      sticky: true,
      render: (_: any, row: any) => (
        <div className="fw-bold text-dark">{row.subjectno || "-"}</div>
      ),
    },
    {
      key: "subjectbarcode",
      label: "Subject Barcode",
      width: "130px",
      textCenter: true,
      sticky: true,
      render: (_: any, row: any) => (
        <div className="fw-semibold text-primary">{row.subjectbarcode || "-"}</div>
      ),
    },
    {
      key: "vaccutainerbarcode",
      label: "Vacutainer Barcode",
      width: "165px",
      textCenter: true,
      sticky: true,
      render: (_: any, row: any) => (
        <div className="font-monospace text-dark">{row.vaccutainerbarcode || "-"}</div>
      ),
    },
    {
      key: "period_number",
      label: "Period",
      width: "90px",
      textCenter: true,
      sticky: true,
      render: (_: any, row: any) => {
        const pNum = row.period_number ? String(row.period_number).padStart(2, "0") : "-";
        return (
          <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 px-2 py-1 font-monospace fw-semibold">
            P:{pNum}
          </span>
        );
      },
    },
    {
      key: "timepoint_number",
      label: "Timepoint",
      width: "110px",
      textCenter: true,
      sticky: true,
      divider: true,
      render: (_: any, row: any) => {
        const tpNum = row.timepoint_number;
        return (
          <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2 py-1 font-monospace fw-semibold">
            T:{tpNum}
          </span>
        );
      },
    },
    {
      key: "dosingscheduledate",
      label: "Schedule Date",
      textCenter: true,
      render: (_: any, row: any) => (
        <div>{row.dosingscheduledate ? formatDate(row.dosingscheduledate) : "-"}</div>
      ),
    },
    {
      key: "dosingscheduletime",
      label: "Schedule Time",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className="font-monospace">{formatTime24Hour(row.dosingscheduletime)}</div>
      ),
    },
    {
      key: "samplecollectiontime",
      label: "Sample Collection Time",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className="font-monospace fw-semibold">
          {row.samplecollectiontime ? formatTime24Hour(row.samplecollectiontime) : "—"}
        </div>
      ),
    },
    {
      key: "totalattempts",
      label: "Total Attempts",
      textCenter: true,
      render: (_: any, row: any) => (
        <span
          className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2.5 py-1.5 fw-bold cursor-pointer"
          style={{ cursor: "pointer", transition: "all 0.2s ease" }}
          onClick={() => handleOpenAttemptsModal(row)}
          title="Click to view Total Attempts History"
        >
          <i className="ri-history-line me-1"></i>
          {row.totalattempts ?? 1}
        </span>
      ),
    },
    {
      key: "deviation",
      label: "Deviation",
      textCenter: true,
      render: (_: any, row: any) => renderDeviationBadge(row),
    },
    {
      key: "deviationstatus",
      label: "Deviation Status",
      textCenter: true,
      render: (_: any, row: any) => {
        const devStatus = row.deviationstatus;
        if (!devStatus) return <span className="text-muted">-</span>;

        const lower = String(devStatus).trim().toLowerCase();
        let badgeColor = "secondary";

        if (lower.includes("exceed") || lower.includes("missed") || lower.includes("error")) {
          badgeColor = "danger";
        } else if (lower.includes("early")) {
          badgeColor = "warning";
        } else if (lower.includes("within") || lower.includes("normal") || lower.includes("no deviation")) {
          badgeColor = "success";
        }

        return (
          <span
            className={`badge bg-${badgeColor} bg-opacity-10 text-${badgeColor} border border-${badgeColor} border-opacity-25 px-2 py-1 fw-semibold`}
            style={{ fontSize: "11px", minWidth: "110px", display: "inline-block", textAlign: "center" }}
          >
            {devStatus}
          </span>
        );
      },
    },
    {
      key: "deviationreason",
      label: "Deviation Reason",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className={`${ac.cellText}`}>
          {row.deviationreason || <span className="text-muted">-</span>}
        </div>
      ),
    },
    {
      key: "dosingstationno",
      label: "Dosing Station No",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className="text-muted">{row.collection_dosingstationno || row.dosingstationno || "-"}</div>
      ),
    },
    {
      key: "scanbyname",
      label: "Last Scanned By",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className="fw-semibold text-dark">{row.scanbyname || "—"}</div>
      ),
    },
    {
      key: "scandate",
      label: "Scanned Date & Time",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className="text-muted small">
          {row.scandate ? `${formatDate(row.scandate)} (${formatTime24Hour(row.scandate)})` : "—"}
        </div>
      ),
    },
    {
      key: "result",
      label: "Status",
      textCenter: true,
      render: (_: any, row: any) => renderResultBadge(row.result),
    },
  ];

  return (
    <>
      <CommonTableIndexWise
        columns={columns}
        data={data}
        loading={loading}
        pageSize={pageSize}
        currentPage={currentPage}
        totalRecords={totalRecords}
        onPageChange={onPageChange}
        actions={{}}
      />

      {selectedAttemptRow && (
        <TotalAttemptsHistoryModal
          isOpen={isAttemptsModalOpen}
          toggle={() => setIsAttemptsModalOpen(!isAttemptsModalOpen)}
          type="collection"
          sessionid={selectedAttemptRow.sessionid}
          subjectbarcode={selectedAttemptRow.subjectbarcode}
          subjectno={selectedAttemptRow.subjectno}
        />
      )}
    </>
  );
};

export default SampleCollectionHistoryTab;
